import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { AuthBrandPanel } from "./LoginPage";

const inputClass =
  "h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-bone pt-[72px] lg:grid-cols-2" data-testid="forgot-password-page">
      <AuthBrandPanel />
      <div className="flex items-center justify-center px-4 py-16 sm:px-8">
        <div className="w-full max-w-md">
          {sent ? (
            <div data-testid="forgot-password-success">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gold/15 text-gold-dark">
                <MailCheck className="h-6 w-6" />
              </span>
              <h1 className="mt-5 font-display text-2xl font-extrabold text-forest-deep">Check your inbox</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                If an account exists for <span className="font-semibold text-forest">{email}</span>, a secure
                reset link is on its way. It expires in one hour.
              </p>
            </div>
          ) : (
            <>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">Account recovery</p>
              <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-forest-deep">
                Reset your password
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Enter your account email and we'll send you a secure reset link.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-password-form">
                <input data-testid="forgot-email-input" type="email" required autoComplete="email"
                  className={inputClass} placeholder="Email address"
                  value={email} onChange={(e) => setEmail(e.target.value)} />
                {error && (
                  <p data-testid="forgot-error" className="rounded-xl bg-destructive/10 px-4 py-2.5 text-sm font-medium text-destructive">
                    {error}
                  </p>
                )}
                <button type="submit" data-testid="forgot-submit-btn" disabled={loading}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-forest font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send reset link
                </button>
              </form>
            </>
          )}
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Remembered it?{" "}
            <Link to="/login" data-testid="back-to-login-link" className="font-semibold text-forest hover:text-gold-dark">
              Back to login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
